import { useState, useEffect, useRef, useCallback } from "react"
import axios from "axios"
import { server } from "../server"

const getConfig = () => {
  const token = localStorage.getItem("token")
  return {
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  }
}

const useSalesBatch = ({ shopId, filters = {}, limit = 50 }) => {
  const [data, setData] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const cacheRef = useRef({})
  const filtersKeyRef = useRef("")

  const buildFiltersKey = useCallback(() => {
    return JSON.stringify({ shopId, ...filters, limit })
  }, [shopId, filters, limit])

  // Reset cache and page when filters change
  useEffect(() => {
    const newKey = buildFiltersKey()
    if (newKey !== filtersKeyRef.current) {
      filtersKeyRef.current = newKey
      cacheRef.current = {}
      setPage(1)
      setData([])
      setTotal(0)
      setTotalPages(0)
    }
  }, [buildFiltersKey])

  // Fetch data for current page
  useEffect(() => {
    if (!shopId) return

    const fetchPage = async () => {
      // Check cache first
      if (cacheRef.current[page]) {
        setData(cacheRef.current[page].data)
        setTotal(cacheRef.current[page].total)
        setTotalPages(cacheRef.current[page].totalPages)
        return
      }

      setIsLoading(true)
      try {
        const params = new URLSearchParams({
          page: page.toString(),
          limit: limit.toString(),
        })

        if (filters.startDate) params.append("startDate", filters.startDate)
        if (filters.endDate) params.append("endDate", filters.endDate)
        if (filters.search) params.append("search", filters.search)
        if (filters.paymentMethod) params.append("paymentMethod", filters.paymentMethod)
        if (filters.paymentStatus) params.append("paymentStatus", filters.paymentStatus)

        const response = await axios.get(
          `${server}/sales/paginated/${shopId}?${params.toString()}`,
          getConfig()
        )

        if (response.data.success) {
          const result = {
            data: response.data.data,
            total: response.data.total,
            totalPages: response.data.totalPages,
          }

          // Cache the result
          cacheRef.current[page] = result

          setData(result.data)
          setTotal(result.total)
          setTotalPages(result.totalPages)
        }
      } catch (error) {
        console.error("Error fetching paginated sales:", error)
        setData([])
      } finally {
        setIsLoading(false)
      }
    }

    fetchPage()
  }, [shopId, page, limit, filters, buildFiltersKey])

  const invalidateCache = useCallback(() => {
    cacheRef.current = {}
  }, [])

  return {
    data,
    isLoading,
    page,
    setPage,
    total,
    totalPages,
    invalidateCache,
  }
}

export default useSalesBatch
